/** Marketing i18n fragment — ucTicketQuestions (use-cases/slack-jira-ticket-questions). */

export const ucTicketQuestionsEn = {
  shot: {
    label: "Support issues",
    alt: "The FixControl issues board — support tickets from Slack and email, each with priority, impact and inline governed actions.",
  },
  meta: {
    title: "Ask FixControl about Jira, Freshdesk, Odoo and TOPdesk tickets from Slack & Teams",
    description:
      "Ask FixControl from a Slack or Teams thread which tickets are open in Jira, Freshdesk, Odoo, TOPdesk or FixControl, what their status is, and which are waiting for approval. An intent gate keeps questions from being filed as new tickets.",
  },
  hero: {
    eyebrow: "Ticket questions",
    title: "Ask about open tickets from Slack or Teams —",
    highlight: "without filing another one",
    subtitle:
      "“What's the status of FIX-212?” costs someone on your support team minutes of searching across systems. FixControl answers it in seconds, inside the Slack or Teams thread where it's asked — from Jira, Freshdesk, Odoo, TOPdesk and its own tickets. Ask follow-ups, or close, reprioritise and assign with an explicit confirmation. A pre-intake check keeps a question from accidentally becoming a new ticket.",
    primaryCta: "Book a demo",
    secondaryCta: "Talk to us",
    bullet1: "No accidental intake",
    bullet2: "Stays inside your own organization",
    bullet3: "Follow-ups keep context",
  },
  how: {
    eyebrow: "How it works",
    title: "A question gets an answer, not a ticket",
    description:
      "The same governed pipeline that handles intake recognizes when you’re asking about work that already exists.",
  },
  steps: {
    ask: {
      tag: "Ask in the thread",
      title: "Ask a question in plain language",
      body: "“Which tickets are still open?”, “What’s the status of FIX-83?”, “Which are waiting for approval?” — asked in the Slack or Teams thread where your team already works.",
    },
    intent: {
      tag: "Intent gate",
      title: "An intent gate checks the question first",
      body: "Before intake runs, a pre-intake intent gate distinguishes a question about existing work from a request to create new work. Asking about tickets doesn’t open one.",
    },
    lookup: {
      tag: "Scoped lookup",
      title: "It reads across FixControl, Jira, Freshdesk, Odoo and TOPdesk",
      body: "FixControl queries the sources your project is bound to, within your own organization and your permissions. The answer comes back compact, with ticket keys and status.",
    },
    followup: {
      tag: "Thread-aware",
      title: "Follow-ups stay in context",
      body: "Ask “which of those are high priority?” and FixControl narrows the previous result set instead of starting a new search.",
    },
  },
  features: {
    eyebrow: "What you can ask",
    title: "Status, priority and approval queues, from the thread",
    description:
      "Answers stay within your own organization, your permissions and the channel’s visibility.",
    status: {
      title: "Status, priority and approval queues",
      body: "Ask what’s open, what a specific key like FIX-83 or KAN-8 is doing, which items are high priority, and which are waiting at a human approval gate.",
    },
    noCreate: {
      title: "No accidental ticket creation",
      body: "FixControl only files a new issue when someone explicitly asks for one. The intent gate keeps lookups and intake on separate paths.",
    },
    partial: {
      title: "Partial results are labeled as partial",
      body: "If Jira, Freshdesk, Odoo or TOPdesk is unreachable, the answer names the source that failed and returns what could be read from the rest.",
    },
    scope: {
      title: "Tenant scope and redaction",
      body: "Answers respect tenant isolation and the project’s knowledge scope. In public or shared channels, sensitive detail is redacted to what that audience is allowed to see.",
    },
    permission: {
      title: "Permission-aware answers",
      body: "What someone on your team can ask about follows their role and the channel they ask from. The lookup reads only what that person is entitled to see.",
    },
    surfaces: {
      title: "Slack and Microsoft Teams",
      body: "The same governed answering works from Slack and Microsoft Teams threads. If your organization works in one or the other, nobody needs a new tool to ask.",
    },
  },
  governed: {
    eyebrow: "Where answers can reach",
    title: "Read-only, and scoped to the asker",
    noteTitle: "Scope, permissions and redaction",
    noteBody:
      "Ticket-question answering runs inside your own organization’s isolated data and the project’s knowledge scope. It reads only sources your project is bound to, honors the asker’s role, and redacts sensitive detail in public or shared channels. It never reaches internal ticket data from the public website chat.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions teams ask before enabling it",
    q1: {
      q: "Does asking about tickets create a new ticket?",
      a: "No. A pre-intake intent gate separates a question about existing work from a request to create new work, and a new issue is filed only when someone explicitly asks for one.",
    },
    q2: {
      q: "Which sources can it answer about?",
      a: "FixControl issues, plus the Jira, Freshdesk, Odoo and TOPdesk sources your project is bound to. Results come back with ticket keys and status so you can act on them.",
    },
    q3: {
      q: "What happens if Jira, Freshdesk, Odoo or TOPdesk is unavailable?",
      a: "FixControl returns the results it could read and tells you which source was unreachable. You know the picture is partial before you act on it.",
    },
    q4: {
      q: "Can it handle thread follow-ups like ‘which of those are high priority?’",
      a: "Yes. Follow-ups run against the previous result set in the same thread; ‘which of those are high priority?’ narrows the earlier answer.",
    },
    q5: {
      q: "Can people see tickets they shouldn’t in a shared channel?",
      a: "Answers stay inside your own organization’s data, and respect the asker’s permissions and the channel’s visibility. In public or shared channels, sensitive detail is redacted before the answer is posted.",
    },
    q6: {
      q: "Can the public website chat answer about internal tickets?",
      a: "No. The public website chat on your own site cannot reach internal ticket data. Ticket-question answering runs in authenticated Slack, Teams or in-app sessions only.",
    },
  },
  related: {
    approvedReplies: {
      label: "Approved replies",
      description: "Draft with AI, approve exactly what gets sent to Freshdesk or Jira.",
    },
    slack: {
      label: "Slack integration",
      description: "Intake, ticket questions, approvals and decision cards in Slack.",
    },
    jira: {
      label: "Jira integration",
      description: "Read and search issues, with per-capability health.",
    },
  },
  cta: {
    title: "See it answer real ticket questions",
    description:
      "Book a demo and ask about live tickets across Slack, Jira, Freshdesk, Odoo and TOPdesk: status, priority and approval queues.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const ucTicketQuestionsNl = {
  shot: {
    label: "Supportissues",
    alt: "Het FixControl issues-overzicht — supporttickets uit Slack en e-mail, elk met prioriteit, impact en inline gegoverneerde acties.",
  },
  meta: {
    title: "Vraag FixControl vanuit Slack & Teams naar Jira-, Freshdesk-, Odoo- en TOPdesk-tickets",
    description:
      "Vraag FixControl vanuit een Slack- of Teams-thread welke tickets openstaan in Jira, Freshdesk, Odoo, TOPdesk of FixControl, wat hun status is, en welke wachten op goedkeuring. Een intent-gate voorkomt dat vragen als nieuwe tickets worden ingeschoten.",
  },
  hero: {
    eyebrow: "Ticketvragen",
    title: "Vraag naar open tickets vanuit Slack of Teams —",
    highlight: "zonder nóg een ticket aan te maken",
    subtitle:
      "“Wat is de status van FIX-212?” kost iemand van je supportteam minuten zoekwerk over systemen heen. FixControl beantwoordt het in seconden, ín de Slack- of Teams-thread waar de vraag gesteld wordt — vanuit Jira, Freshdesk, Odoo, TOPdesk en de eigen tickets. Stel vervolgvragen, of sluit, herprioriteer en wijs toe met een expliciete bevestiging. Een pre-intake-check voorkomt dat een vraag per ongeluk een nieuw ticket wordt.",
    primaryCta: "Boek een demo",
    secondaryCta: "Neem contact op",
    bullet1: "Geen onbedoelde intake",
    bullet2: "Blijft binnen je eigen organisatie",
    bullet3: "Vervolgvragen behouden context",
  },
  how: {
    eyebrow: "Hoe het werkt",
    title: "Een vraag krijgt een antwoord, geen ticket",
    description:
      "Dezelfde gegoverneerde pijplijn die intake afhandelt, herkent wanneer je vraagt naar werk dat al bestaat.",
  },
  steps: {
    ask: {
      tag: "Vraag in de thread",
      title: "Stel een vraag in gewone taal",
      body: "“Welke tickets staan nog open?”, “Wat is de status van FIX-83?”, “Welke wachten op goedkeuring?” — gesteld in de Slack- of Teams-thread waar je team al werkt.",
    },
    intent: {
      tag: "Intent-gate",
      title: "Een intent-gate controleert de vraag eerst",
      body: "Voordat intake draait, onderscheidt een pre-intake intent-gate een vraag over bestaand werk van een verzoek om nieuw werk aan te maken. Vragen naar tickets openen er geen.",
    },
    lookup: {
      tag: "Gescopete lookup",
      title: "Het leest over FixControl, Jira, Freshdesk, Odoo en TOPdesk heen",
      body: "FixControl bevraagt de bronnen waaraan je project is gekoppeld, binnen je eigen organisatie en je permissies. Het antwoord komt compact terug, met ticketsleutels en status.",
    },
    followup: {
      tag: "Thread-bewust",
      title: "Vervolgvragen blijven in context",
      body: "Vraag “welke daarvan zijn hoge prioriteit?” en FixControl verfijnt de vorige resultatenset in plaats van een nieuwe zoekopdracht te starten.",
    },
  },
  features: {
    eyebrow: "Wat je kunt vragen",
    title: "Status, prioriteit en goedkeuringswachtrijen, vanuit de thread",
    description:
      "Antwoorden blijven binnen je eigen organisatie, je permissies en de zichtbaarheid van het kanaal.",
    status: {
      title: "Status, prioriteit en goedkeuringswachtrijen",
      body: "Vraag wat openstaat, wat een specifieke sleutel als FIX-83 of KAN-8 doet, welke items hoge prioriteit hebben, en welke wachten op een menselijke goedkeuringspoort.",
    },
    noCreate: {
      title: "Geen onbedoelde ticketaanmaak",
      body: "FixControl maakt alleen een nieuw issue aan wanneer iemand daar expliciet om vraagt. De intent-gate houdt lookups en intake op gescheiden paden.",
    },
    partial: {
      title: "Deelresultaten worden als deel gemarkeerd",
      body: "Als Jira, Freshdesk, Odoo of TOPdesk onbereikbaar is, benoemt het antwoord de bron die faalde en geeft het terug wat uit de rest gelezen kon worden.",
    },
    scope: {
      title: "Tenant-scope en redactie",
      body: "Antwoorden respecteren tenant-isolatie en de kennisscope van het project. In publieke of gedeelde kanalen wordt gevoelige informatie geredigeerd tot wat dat publiek mag zien.",
    },
    permission: {
      title: "Permissie-bewuste antwoorden",
      body: "Waar iemand van je team naar mag vragen volgt zijn rol en het kanaal van waaruit hij vraagt. De lookup leest alleen wat die persoon mag zien.",
    },
    surfaces: {
      title: "Slack en Microsoft Teams",
      body: "Hetzelfde gegoverneerde beantwoorden werkt vanuit Slack- en Microsoft Teams-threads. Werkt je organisatie in het ene of het andere kanaal, dan heeft niemand een nieuwe tool nodig om te vragen.",
    },
  },
  governed: {
    eyebrow: "Waar antwoorden kunnen reiken",
    title: "Alleen-lezen, en gescoped tot de vrager",
    noteTitle: "Scope, permissies en redactie",
    noteBody:
      "Het beantwoorden van ticketvragen draait binnen de afgeschermde gegevens van je eigen organisatie en de kennisscope van het project. Het leest alleen bronnen waaraan je project is gekoppeld, respecteert de rol van de vrager en redigeert gevoelige informatie in publieke of gedeelde kanalen. Het bereikt nooit interne ticketdata vanuit de publieke website-chat.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen die teams stellen voordat ze het inschakelen",
    q1: {
      q: "Maakt een vraag over tickets een nieuw ticket aan?",
      a: "Nee. Een pre-intake intent-gate scheidt een vraag over bestaand werk van een verzoek om nieuw werk aan te maken. FixControl maakt alleen een nieuw issue aan wanneer iemand daar expliciet om vraagt.",
    },
    q2: {
      q: "Over welke bronnen kan het antwoorden?",
      a: "FixControl-issues plus de Jira-, Freshdesk-, Odoo- en TOPdesk-bronnen waaraan je project is gekoppeld. Resultaten komen terug met ticketsleutels en status, zodat je ernaar kunt handelen.",
    },
    q3: {
      q: "Wat gebeurt er als Jira, Freshdesk, Odoo of TOPdesk niet beschikbaar is?",
      a: "FixControl geeft de resultaten terug die het kon lezen en vertelt je welke bron onbereikbaar was. Je weet dat het beeld deels is voordat je erop handelt.",
    },
    q4: {
      q: "Kan het thread-vervolgvragen aan zoals ‘welke daarvan zijn hoge prioriteit?’",
      a: "Ja. Vervolgvragen draaien tegen de vorige resultatenset in dezelfde thread; ‘welke daarvan zijn hoge prioriteit?’ verfijnt het eerdere antwoord.",
    },
    q5: {
      q: "Kunnen mensen tickets zien die ze niet mogen zien in een gedeeld kanaal?",
      a: "Antwoorden blijven binnen de gegevens van je eigen organisatie en respecteren de permissies van de vrager en de zichtbaarheid van het kanaal. In publieke of gedeelde kanalen wordt gevoelige informatie geredigeerd voordat het antwoord wordt geplaatst.",
    },
    q6: {
      q: "Kan de publieke website-chat over interne tickets antwoorden?",
      a: "Nee. De publieke website-chat op je eigen site kan geen interne ticketdata bereiken. Het beantwoorden van ticketvragen draait uitsluitend in geauthenticeerde Slack-, Teams- of in-app-sessies.",
    },
  },
  related: {
    approvedReplies: {
      label: "Goedgekeurde antwoorden",
      description: "Stel op met AI, keur precies goed wat naar Freshdesk of Jira gaat.",
    },
    slack: {
      label: "Slack-integratie",
      description: "Intake, ticketvragen, goedkeuringen en beslis-kaarten in Slack.",
    },
    jira: {
      label: "Jira-integratie",
      description: "Lees en doorzoek issues, met capability-health.",
    },
  },
  cta: {
    title: "Zie het echte ticketvragen beantwoorden",
    description:
      "Boek een demo en vraag naar live tickets over Slack, Jira, Freshdesk, Odoo en TOPdesk: status, prioriteit en goedkeuringswachtrijen.",
    primary: "Boek een demo",
    secondary: "Neem contact op",
  },
};
